import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { sentry } from '../services/sentry';
import { getApiBaseUrl } from '../utils/env';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors({});
    }
    if (serverError) {
      setServerError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email address is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setServerError('');

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await axios.post(`${apiBaseUrl}/auth/forgot-password`, {
        email: email
      });

      const { success } = response.data;

      if (success) {
        setIsSuccess(true);
        
        // Log event
        sentry.captureMessage('Password reset requested', {
          email: email
        });
      }
    } catch (error) {
      console.error('Forgot password error:', error);

      const requestId = error.response?.headers?.['x-request-id'] || 'N/A';
      const errorMessage = error.response?.data?.error || 'Failed to send reset email. Please try again.';
      
      setServerError(`${errorMessage} (Request ID: ${requestId})`);

      // Log error to Sentry
      sentry.captureException(error, {
        email: email,
        requestId,
        statusCode: error.response?.status
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-card">
            <div className="success-icon">✅</div>
            <h1>Check your email</h1>
            <p className="success-message">
              If an account exists with that email, we've sent password reset instructions to:
            </p>
            <p className="email-display">{email}</p>
            <p className="help-text">
              Please check your inbox and spam folder. The link will expire in 1 hour.
            </p>
            <Link to="/login" className="back-button">
              Back to Sign In
            </Link>
            <div className="resend-text">
              Didn't receive the email? <button onClick={() => setIsSuccess(false)} className="link-button">Try again</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-card">
          {/* Header */}
          <div className="forgot-password-header">
            <a href="/" className="logo" aria-label="CerebralMachines home">
              <img src="/CMLogo.png" alt="CerebralMachines" />
            </a>
            <h1>Forgot your password?</h1>
            <p>No worries, we'll send you reset instructions.</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="forgot-password-form">
            {serverError && (
              <div className="error-banner">
                {serverError}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email"
                disabled={isLoading}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <button
              type="submit"
              className={`submit-button ${isLoading ? 'loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <Link to="/login" className="back-link">
              ← Back to Sign In
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;



